import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Radio, Play, Pause, Send, Users, Clock, Calendar,
  Mic, Headphones, Volume2, MessageCircle, X, ChevronRight,
  ExternalLink, Share2, Bell, BellOff, Maximize2, Video, Download
} from 'lucide-react';
import { PageEditProvider } from '@/context/PageEditModeContext';
import { usePageEditMode } from '@/context/PageEditModeContext';
import { PageEditable } from '@/components/edit/PageEditable';
import { PageEditToolbar } from '@/components/edit/PageEditToolbar';
import { AdminEditorLink } from '@/components/edit/AdminEditorLink';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Live Status Badge Component
const LiveBadge = ({ isLive }) => (
  <motion.div
    initial={{ scale: 0.8, opacity: 0 }}
    animate={{ scale: 1, opacity: 1 }}
    className={`inline-flex items-center gap-2 px-4 py-2 rounded-full font-bold text-sm ${
      isLive 
        ? 'bg-red-500 text-white animate-pulse' 
        : 'bg-card border border-border text-muted-foreground'
    }`}
  >
    <span className={`w-3 h-3 rounded-full ${isLive ? 'bg-white animate-ping' : 'bg-muted-foreground'}`} />
    {isLive
      ? <PageEditable path="hero.liveBadge" as="span" fallback="LIVE NOW" />
      : <PageEditable path="hero.offlineBadge" as="span" fallback="OFFLINE" />}
  </motion.div>
);

// Chat Message Component
const ChatMessageItem = ({ message }) => (
  <motion.div
    initial={{ opacity: 0, x: -20 }}
    animate={{ opacity: 1, x: 0 }}
    className="flex gap-3 py-2"
  >
    <div 
      className="w-8 h-8 rounded-full flex items-center justify-center text-black font-bold text-sm flex-shrink-0"
      style={{ backgroundColor: message.user_color || '#FFB800' }}
    >
      {message.username[0]?.toUpperCase()}
    </div>
    <div className="flex-1 min-w-0">
      <span className="font-bold text-sm" style={{ color: message.user_color || '#FFB800' }}>
        {message.username}
      </span>
      <p className="text-foreground text-sm break-words">{message.message}</p>
    </div>
  </motion.div>
);

// Inline-editable text field for podcast cards
const InlineField = ({ value, editMode, onSave, className = '', placeholder = '', as: Tag = 'span', multiline = false }) => {
  if (!editMode) {
    return <Tag className={className}>{value || placeholder}</Tag>;
  }
  return (
    <Tag
      className={`${className} outline-dashed outline-2 outline-yellow-400/70 outline-offset-2 rounded-sm cursor-text focus:outline-primary focus:outline-solid`}
      contentEditable
      suppressContentEditableWarning
      spellCheck
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
      onBlur={(e) => {
        const text = multiline ? e.currentTarget.innerText : e.currentTarget.textContent;
        onSave((text || '').trim());
      }}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (!multiline && e.key === 'Enter') {
          e.preventDefault();
          e.currentTarget.blur();
        }
      }}
    >
      {value || placeholder}
    </Tag>
  );
};

// Podcast Episode Card
const PodcastCard = ({ episode, onPlay, isPlaying, onRecordPlay, editMode, onFieldSave }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    className="bg-card border border-border/50 rounded-2xl overflow-hidden hover:border-primary/30 transition-all group"
    data-testid={`podcast-card-${episode.id}`}
  >
    <div className="relative aspect-video">
      {episode.thumbnail ? (
        <img loading="lazy" decoding="async" src={episode.thumbnail} alt={episode.title} className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center">
          <Mic className="w-16 h-16 text-primary/50" />
        </div>
      )}
      {!editMode && (
        <button
          onClick={() => {
            onPlay(episode);
            if (onRecordPlay && !isPlaying) onRecordPlay(episode.id);
          }}
          className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity"
          data-testid={`podcast-play-btn-${episode.id}`}
        >
          <div className="w-16 h-16 rounded-full bg-primary flex items-center justify-center shadow-glow transform group-hover:scale-110 transition-transform">
            {isPlaying ? <Pause className="w-8 h-8 text-black" /> : <Play className="w-8 h-8 text-black ml-1" />}
          </div>
        </button>
      )}
      {episode.duration && (
        <span className="absolute bottom-3 right-3 px-2 py-1 bg-black/70 rounded text-xs text-white">
          {episode.duration}
        </span>
      )}
      {episode.is_video && (
        <span className="absolute top-3 left-3 px-2 py-1 bg-red-500/80 rounded text-xs text-white flex items-center gap-1">
          <Video className="w-3 h-3" /> <PageEditable path="podcasts.videoBadge" as="span" fallback="Video" />
        </span>
      )}
    </div>
    <div className="p-5">
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        {(episode.category || editMode) && (
          <InlineField
            value={episode.category}
            editMode={editMode}
            placeholder="Category"
            className="text-xs px-2 py-0.5 bg-primary/20 text-primary rounded-full"
            onSave={(v) => onFieldSave(episode.id, 'category', v)}
          />
        )}
        {(episode.series || editMode) && (
          <InlineField
            value={episode.series}
            editMode={editMode}
            placeholder="Series"
            className="text-xs text-muted-foreground"
            onSave={(v) => onFieldSave(episode.id, 'series', v)}
          />
        )}
      </div>
      <InlineField
        as="h3"
        value={episode.title}
        editMode={editMode}
        placeholder="Episode title"
        className="font-bold text-lg text-foreground mb-2 line-clamp-2 block"
        onSave={(v) => onFieldSave(episode.id, 'title', v)}
      />
      <InlineField
        as="p"
        value={episode.description}
        editMode={editMode}
        placeholder="Episode description"
        multiline
        className="text-sm text-muted-foreground line-clamp-2 mb-3 block"
        onSave={(v) => onFieldSave(episode.id, 'description', v)}
      />
      <div className="flex items-center gap-4 text-xs text-muted-foreground">
        <span className="flex items-center gap-1">
          <Calendar className="w-3 h-3" />
          {new Date(episode.published_at).toLocaleDateString()}
        </span>
        {episode.duration && (
          <span className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {episode.duration}
          </span>
        )}
      </div>
    </div>
  </motion.div>
);

// Countdown Timer Component
const CountdownTimer = ({ targetDate }) => {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    if (!targetDate) return;
    
    const timer = setInterval(() => {
      const now = new Date().getTime();
      const target = new Date(targetDate).getTime();
      const difference = target - now;

      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((difference % (1000 * 60)) / 1000)
        });
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [targetDate]);

  const TimeBlock = ({ value, label }) => (
    <div className="flex flex-col items-center">
      <div className="w-16 h-16 md:w-20 md:h-20 bg-card border border-border/50 rounded-xl flex items-center justify-center">
        <span className="text-2xl md:text-3xl font-bold text-primary">{String(value).padStart(2, '0')}</span>
      </div>
      <span className="text-xs text-muted-foreground mt-2 uppercase tracking-wider">{label}</span>
    </div>
  );

  return (
    <div className="flex gap-4 justify-center">
      <TimeBlock value={timeLeft.days} label="Days" />
      <TimeBlock value={timeLeft.hours} label="Hours" />
      <TimeBlock value={timeLeft.minutes} label="Mins" />
      <TimeBlock value={timeLeft.seconds} label="Secs" />
    </div>
  );
};

// Main Live Page Component
const LiveInner = () => {
  const { getValue, enabled: editMode } = usePageEditMode();
  const t = (path, fallback) => getValue(path) || fallback;
  const [liveStatus, setLiveStatus] = useState({ is_live: false });
  const [chatMessages, setChatMessages] = useState([]);
  const [podcasts, setPodcasts] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [username, setUsername] = useState(() => localStorage.getItem('chat_username') || '');
  const [showUsernameModal, setShowUsernameModal] = useState(false);
  const [chatOpen, setChatOpen] = useState(false); // Start with chat closed
  const [viewerCount, setViewerCount] = useState(0);
  const [notifications, setNotifications] = useState(true);
  const [currentPodcast, setCurrentPodcast] = useState(null);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const chatEndRef = useRef(null);
  const chatPollRef = useRef(null);
  const audioRef = useRef(null);

  // Fetch live status
  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/live/status`);
        const data = await res.json();
        setLiveStatus(data);
        // Simulate viewer count
        if (data.is_live) {
          setViewerCount(Math.floor(Math.random() * 50) + 10);
        }
      } catch (err) {
        console.error('Failed to fetch live status:', err);
      }
    };
    fetchStatus();
    const interval = setInterval(fetchStatus, 10000); // Poll every 10s
    return () => clearInterval(interval);
  }, []);

  // Fetch chat messages
  useEffect(() => {
    const fetchChat = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/live/chat`);
        const data = await res.json();
        setChatMessages(data);
      } catch (err) {
        console.error('Failed to fetch chat:', err);
      }
    };
    fetchChat();
    // Poll chat when live
    if (liveStatus.is_live) {
      chatPollRef.current = setInterval(fetchChat, 2000);
    }
    return () => {
      if (chatPollRef.current) clearInterval(chatPollRef.current);
    };
  }, [liveStatus.is_live]);

  // Fetch podcasts
  useEffect(() => {
    const fetchPodcasts = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/podcasts`);
        const data = await res.json();
        // Map API response to component format
        const formattedPodcasts = (data.podcasts || []).map(p => ({
          id: p.id,
          title: p.title,
          description: p.description,
          thumbnail: p.cover_url,
          audio_url: p.audio_url,
          video_url: p.video_url,
          embed_url: p.embed_url,
          duration: p.duration,
          published_at: p.created_at,
          category: p.category,
          series: p.series,
          is_video: p.is_video,
          members_only_download: p.members_only_download
        }));
        setPodcasts(formattedPodcasts);
      } catch (err) {
        console.error('Failed to fetch podcasts:', err);
      }
    };
    fetchPodcasts();
  }, []);

  // Auto-scroll chat - only scroll chat container, not page
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' });
    }
  }, [chatMessages]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;
    
    if (!username) {
      setShowUsernameModal(true);
      return;
    }

    try {
      await fetch(`${BACKEND_URL}/api/live/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, message: newMessage })
      });
      setNewMessage('');
      // Immediately fetch new messages
      const res = await fetch(`${BACKEND_URL}/api/live/chat`);
      const data = await res.json();
      setChatMessages(data);
    } catch (err) {
      console.error('Failed to send message:', err);
    }
  };

  const handleSetUsername = (e) => {
    e.preventDefault();
    if (username.trim()) {
      localStorage.setItem('chat_username', username);
      setShowUsernameModal(false);
    }
  };

  // Record play for stats
  const recordPlay = async (podcastId) => {
    try {
      await fetch(`${BACKEND_URL}/api/podcasts/${podcastId}/play`, { method: 'POST' });
    } catch (err) {
      console.error('Failed to record play:', err);
    }
  };

  const handlePlayPodcast = (episode) => {
    if (currentPodcast?.id === episode.id) {
      // Toggle play/pause for current episode
      if (audioRef.current) {
        if (isAudioPlaying) {
          audioRef.current.pause();
          setIsAudioPlaying(false);
        } else {
          audioRef.current.play();
          setIsAudioPlaying(true);
        }
      }
    } else {
      // Switch to new episode
      setCurrentPodcast(episode);
      setIsAudioPlaying(true);
    }
  };

  // Handle audio element events
  useEffect(() => {
    if (currentPodcast && audioRef.current) {
      const audioUrl = currentPodcast.audio_url || currentPodcast.video_url;
      if (audioUrl) {
        audioRef.current.src = audioUrl;
        audioRef.current.play().catch(err => console.log('Autoplay prevented:', err));
        setIsAudioPlaying(true);
      }
    }
  }, [currentPodcast?.id]);

  const handleClosePlayer = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
    }
    setCurrentPodcast(null);
    setIsAudioPlaying(false);
  };

  // Inline save handler for podcast card fields → PUT /api/podcasts/{id}
  const handlePodcastFieldSave = async (episodeId, field, value) => {
    // Optimistic UI update
    setPodcasts((prev) => prev.map((p) => (p.id === episodeId ? { ...p, [field]: value } : p)));
    try {
      const res = await fetch(`${BACKEND_URL}/api/podcasts/${episodeId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ [field]: value }),
      });
      if (!res.ok) throw new Error('save failed');
    } catch (err) {
      console.error('Podcast inline save failed:', err);
    }
  };

  // Twitch channel for 24/7 livestream
  const TWITCH_CHANNEL = 'mestrecurtispierre';
  
  // Get parent domain for Twitch embed (required by Twitch)
  const getParentDomain = () => {
    return window.location.hostname;
  };

  // Extract video embed URL for various platforms
  const getEmbedUrl = (url) => {
    if (!url) return null;
    
    // Handle Twitch URLs
    if (url.includes('twitch.tv')) {
      const channel = url.split('/').pop()?.split('?')[0];
      return {
        type: 'twitch',
        url: `https://player.twitch.tv/?channel=${channel}&parent=${getParentDomain()}&autoplay=true`
      };
    }
    
    // Handle YouTube URLs
    if (url.includes('youtube.com') || url.includes('youtu.be')) {
      const videoId = url.includes('youtu.be') 
        ? url.split('/').pop()?.split('?')[0]
        : new URLSearchParams(new URL(url).search).get('v');
      return {
        type: 'youtube',
        url: `https://www.youtube.com/embed/${videoId}?autoplay=1`
      };
    }
    
    // Handle Vimeo URLs
    if (url.includes('vimeo.com')) {
      const videoId = url.split('/').pop()?.split('?')[0];
      return {
        type: 'vimeo',
        url: `https://player.vimeo.com/video/${videoId}?autoplay=1`
      };
    }
    
    return { type: 'unknown', url };
  };
  
  // Get Twitch embed URL for the 24/7 stream
  const getTwitchEmbedUrl = () => {
    return `https://player.twitch.tv/?channel=${TWITCH_CHANNEL}&parent=${getParentDomain()}&autoplay=true`;
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-12 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-6">
            
            {/* Main Video/Stream Area */}
            <div className="flex-1">
              <div className="bg-card border border-border/50 rounded-3xl overflow-hidden">
                {/* Stream Header */}
                <div className="flex items-center justify-between p-4 border-b border-border/30">
                  <div className="flex items-center gap-4">
                    <LiveBadge isLive={liveStatus.is_live} />
                    {liveStatus.is_live && (
                      <span className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Users className="w-4 h-4" />
                        {viewerCount} <PageEditable path="hero.watchingLabel" as="span" fallback="watching" />
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setNotifications(!notifications)}
                      className={`p-2 rounded-lg transition-colors ${notifications ? 'bg-primary/20 text-primary' : 'bg-card text-muted-foreground'}`}
                      title={notifications ? 'Notifications on' : 'Notifications off'}
                    >
                      {notifications ? <Bell className="w-5 h-5" /> : <BellOff className="w-5 h-5" />}
                    </button>
                    <button className="p-2 hover:bg-white/5 rounded-lg text-muted-foreground transition-colors">
                      <Share2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
                
                {/* Video Player Area - Twitch 24/7 Stream */}
                <div className="relative aspect-video bg-black" style={{ scrollMargin: '0', scrollSnapAlign: 'none' }}>
                  {/* Always show Twitch stream - it's 24/7 */}
                  <iframe
                    src={getTwitchEmbedUrl()}
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                    allowFullScreen
                    data-testid="twitch-stream-embed"
                  />

                  {/* Live indicator overlay when streaming */}
                  {liveStatus.is_live && (
                    <div className="absolute top-4 left-4 z-10">
                      <div className="flex items-center gap-2 px-3 py-1.5 bg-red-600 rounded-md">
                        <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
                        <span className="text-white text-sm font-bold"><PageEditable path="hero.liveOverlay" as="span" fallback="LIVE" /></span>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Stream Info */}
                <div className="p-6">
                  <h1 className="text-2xl font-bold font-display text-foreground mb-2">
                    {liveStatus.is_live
                      ? <PageEditable path="hero.liveTitle" as="span" fallback={liveStatus.stream_title || "Live Stream — Escola de Samba Casa Samba"} />
                      : <PageEditable path="hero.offlineTitle" as="span" fallback="Live Stream" />}
                  </h1>
                  <p className="text-muted-foreground">
                    {liveStatus.is_live
                      ? <PageEditable path="hero.liveDescription" as="span" fallback={liveStatus.stream_description || "Join us for live Afro-Brasilian music, dance, and culture!"} />
                      : <PageEditable path="hero.offlineDescription" as="span" fallback="We're off the air right now. Scroll down for podcast episodes you may also be interested in." />}
                  </p>
                </div>
              </div>
            </div>
            
            {/* Chat Toggle Button */}
            <button
              onClick={() => setChatOpen(!chatOpen)}
              className="fixed right-6 bottom-32 z-40 p-4 bg-primary text-black rounded-full shadow-glow hover:scale-110 transition-transform flex items-center gap-2"
              data-testid="toggle-chat-btn"
              title={chatOpen ? "Close Chat" : "Open Chat"}
            >
              {chatOpen ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
              <span className="hidden md:inline font-bold text-sm">{chatOpen ? <PageEditable path="chat.closeButton" as="span" fallback="Close" /> : <PageEditable path="chat.openButton" as="span" fallback="Chat" />}</span>
            </button>
          </div>
        </div>
      </section>

      {/* Floating Chat Panel - Overlay */}
      <AnimatePresence>
        {chatOpen && (
          <motion.div
            initial={{ x: 400, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: 400, opacity: 0 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="fixed right-4 top-24 bottom-24 w-80 md:w-96 z-50"
          >
            <div className="bg-card border border-border/50 rounded-3xl overflow-hidden h-full flex flex-col shadow-2xl">
              {/* Chat Header */}
              <div className="flex items-center justify-between p-4 border-b border-border/30 bg-card">
                <div className="flex items-center gap-2">
                  <MessageCircle className="w-5 h-5 text-primary" />
                  <h3 className="font-bold text-foreground"><PageEditable path="chat.panelTitle" as="span" fallback="Live Chat" /></h3>
                </div>
                <button
                  onClick={() => setChatOpen(false)}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors bg-red-500/20 hover:bg-red-500/40"
                  title="Close chat"
                  data-testid="close-chat-btn"
                >
                  <X className="w-5 h-5 text-red-400" />
                </button>
              </div>
              
              {/* Chat Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-1">
                {chatMessages.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <MessageCircle className="w-12 h-12 mx-auto mb-4 opacity-30" />
                    <p><PageEditable path="chat.emptyTitle" as="span" fallback="No messages yet" /></p>
                    <p className="text-sm"><PageEditable path="chat.emptyBody" as="span" fallback="Be the first to say hello!" /></p>
                  </div>
                ) : (
                  chatMessages.map((msg) => (
                    <ChatMessageItem key={msg.id} message={msg} />
                  ))
                )}
                <div ref={chatEndRef} />
              </div>
              
              {/* Chat Input */}
              <form onSubmit={handleSendMessage} className="p-4 border-t border-border/30 bg-card">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder={username ? t('chat.inputPlaceholder', 'Send a message...') : t('chat.inputPlaceholderNoUser', 'Set username to chat')}
                    className="flex-1 px-4 py-3 bg-background border border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                    maxLength={500}
                  />
                  <button
                    type="submit"
                    className="px-4 py-3 bg-primary hover:bg-primary/90 text-primary-foreground rounded-xl transition-colors"
                  >
                    <Send className="w-5 h-5" />
                  </button>
                </div>
                {!username && (
                  <button
                    type="button"
                    onClick={() => setShowUsernameModal(true)}
                    className="mt-2 text-sm text-primary hover:underline"
                  >
                    <PageEditable path="chat.setUsernameLink" as="span" fallback="Click here to set your username" />
                  </button>
                )}
              </form>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Podcast Episodes Section */}
      <section className="py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-3xl font-bold font-display text-foreground flex items-center gap-3">
                <Headphones className="w-8 h-8 text-primary" />
                <PageEditable path="podcasts.sectionTitle" as="span" fallback="Also, you might be interested in" />
              </h2>
              <p className="text-muted-foreground mt-1">
                <PageEditable path="podcasts.sectionSubtitle" as="span" fallback="Podcast episodes — catch up on past shows and exclusive content" />
              </p>
            </div>
          </div>

          {podcasts.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {podcasts.map((episode) => (
                <PodcastCard 
                  key={episode.id} 
                  episode={episode} 
                  onPlay={handlePlayPodcast}
                  isPlaying={currentPodcast?.id === episode.id && isAudioPlaying}
                  onRecordPlay={recordPlay}
                  editMode={editMode}
                  onFieldSave={handlePodcastFieldSave}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-16 bg-card border border-border/50 rounded-3xl">
              <Mic className="w-16 h-16 mx-auto mb-4 text-primary/30" />
              <h3 className="text-xl font-bold text-foreground mb-2">
                <PageEditable path="podcasts.emptyTitle" as="span" fallback="Coming Soon!" />
              </h3>
              <p className="text-muted-foreground max-w-md mx-auto">
                <PageEditable path="podcasts.emptyBody" as="span" fallback="We're working on bringing you amazing podcast content. Check back soon or turn on notifications!" />
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Floating Podcast Player */}
      <AnimatePresence>
        {currentPodcast && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            className="fixed bottom-20 md:bottom-0 left-0 right-0 z-50 bg-card border-t border-border/50 p-4"
            data-testid="podcast-player"
          >
            {/* Hidden audio element */}
            <audio 
              ref={audioRef} 
              onEnded={() => setIsAudioPlaying(false)}
              onPause={() => setIsAudioPlaying(false)}
              onPlay={() => setIsAudioPlaying(true)}
            />
            
            <div className="max-w-7xl mx-auto flex items-center gap-4">
              <div className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0">
                {currentPodcast.thumbnail ? (
                  <img loading="lazy" decoding="async" src={currentPodcast.thumbnail} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-primary/20 flex items-center justify-center">
                    <Mic className="w-6 h-6 text-primary" />
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-bold text-foreground truncate">{currentPodcast.title}</h4>
                <p className="text-sm text-muted-foreground truncate">{currentPodcast.description}</p>
              </div>
              <div className="flex items-center gap-2">
                <button 
                  onClick={() => handlePlayPodcast(currentPodcast)}
                  className="p-3 bg-primary hover:bg-primary/90 text-black rounded-full transition-colors"
                  data-testid="podcast-player-toggle"
                >
                  {isAudioPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
                </button>
                <button 
                  onClick={handleClosePlayer}
                  className="p-2 hover:bg-white/10 rounded-lg text-muted-foreground transition-colors"
                  data-testid="podcast-player-close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Username Modal */}
      <AnimatePresence>
        {showUsernameModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-6"
            onClick={() => setShowUsernameModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-card border border-border/50 rounded-2xl p-6 w-full max-w-sm"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-xl font-bold text-foreground mb-4"><PageEditable path="usernameModal.title" as="span" fallback="Set Your Username" /></h3>
              <form onSubmit={handleSetUsername}>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder={t('usernameModal.placeholder', 'Enter your username')}
                  className="w-full px-4 py-3 bg-background border border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary mb-4"
                  maxLength={20}
                  autoFocus
                />
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setShowUsernameModal(false)}
                    className="flex-1 px-4 py-3 border border-border rounded-xl text-foreground hover:bg-white/5 transition-colors"
                  >
                    <PageEditable path="usernameModal.cancelButton" as="span" fallback="Cancel" />
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-3 bg-primary hover:bg-primary/90 text-primary-foreground rounded-xl font-bold transition-colors"
                  >
                    <PageEditable path="usernameModal.saveButton" as="span" fallback="Save" />
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const Live = () => (
  <PageEditProvider slug="live">
    <LiveInner />
    <PageEditToolbar />
    <AdminEditorLink to="/admin/live" label="Edit Live (Dashboard)" />
  </PageEditProvider>
);

export default Live;
